/**
 * PROJECT-OVERVIEW §7.2 — Contrato and Aditivo.
 *
 * 🔒 **BR-21, the rule most likely to be broken.** A contract can be amended
 * mid-term — a CSB addendum, a renegotiated PTAX — and **past periods keep the
 * parameters that were in force at the time**. An `Aditivo` takes effect FORWARD
 * ONLY. Never convert a historical cost with today's PTAX or desconto; any period
 * view must show which parameter set applied.
 *
 * That is why the amendable parameters live in their own object: `Contrato` and
 * `Aditivo` both carry one, so "which parameters were in force" is a value the UI
 * can hold and display rather than a rule it has to remember.
 */
import { z } from 'zod'

import { dataIso, id } from './comum'

/**
 * One variable of the calculation formula — decided by Fábio on 2026-09-17.
 *
 * `tag` is the identifier the formula references (`consumo_usd`, `cotacao_ptax`).
 * `valor` is OPTIONAL: `null` means the backend supplies it at calculation time
 * (e.g. `consumo_usd`, measured per period); a number is a constant agreed in the
 * contract (PTAX, desconto).
 *
 * 🔒 `cotacao_ptax` is still ENTERED MANUALLY — never fetched from the internet
 * (BR-04). It stopped being a named field and became a row here, and the rule came
 * along with it: if a future screen offers to "look this up", it is violating a
 * business rule, not helping.
 */
export const variavelDeContratoSchema = z.object({
  tag: z.string().regex(/^[a-z][a-z0-9_]*$/),
  nome: z.string(),
  descricao: z.string(),
  valor: z.number().finite().nullable(),
})

export type VariavelDeContrato = z.infer<typeof variavelDeContratoSchema>

/** The tags the screens read by name to LABEL a parameter set (never to compute). */
export const TAG_COTACAO_PTAX = 'cotacao_ptax'
export const TAG_DESCONTO_AWS = 'desconto_aws'

/**
 * The constant value of a variable, by tag. `null` when the tag is absent or its
 * value is supplied by the backend — callers must render that as "not shown", never
 * as zero.
 */
export function valorDaVariavel(
  variaveis: readonly VariavelDeContrato[],
  tag: string,
): number | null {
  return variaveis.find((v) => v.tag === tag)?.valor ?? null
}

/**
 * The parameter set an aditivo re-declares. §7.2 writes it inline on both; naming
 * it keeps the two from drifting apart field by field.
 *
 * 🔒 `variaveis` + `formulaCalculo` live HERE, per parameter set (BR-21): an
 * aditivo carries its own variables and formula, effective forward only.
 */
export const parametrosDeContratoSchema = z.object({
  /** CSB = Serpro's unit of cloud consumption; the contract entitlement. */
  quantidadeCsbs: z.number().finite().nonnegative(),
  /** Tags are unique within the set — the formula could not tell two apart. */
  variaveis: z
    .array(variavelDeContratoSchema)
    .refine((lista) => new Set(lista.map((v) => v.tag)).size === lista.length, {
      message: 'tags de variável repetidas',
    }),
  /**
   * Eduardo asked that the formula be written down explicitly, not left implicit.
   * Grammar and validation: `src/modules/orcamento/formula.ts` (server mirrors it).
   */
  formulaCalculo: z.string(),
})

export type ParametrosDeContrato = z.infer<typeof parametrosDeContratoSchema>

export const contratoSchema = parametrosDeContratoSchema.extend({
  idContrato: id,
  idCliente: id,
  /**
   * Descriptive. **"Serpro" unlocks extra fields — WHICH ONES IS OPEN (§9).**
   * Eduardo: *"se for Serpro, ele abre outras possibilidades"*, unspecified.
   * Do not invent them; the field stays a plain string until iOX says more.
   */
  broker: z.string(),
  vigenciaInicio: dataIso,
  vigenciaFim: dataIso,
  /**
   * e.g. `20` → the period runs from day 20 to day 19 of the next month (BR-03).
   * // ⚠️ assumido — §9 (the field exists in §7.2 as our reading; the backend has
   * not confirmed that the fiscal month is expressed as a start day).
   */
  mesFiscalDiaInicio: z.number().int().min(1).max(31),
})

export type Contrato = z.infer<typeof contratoSchema>

export const aditivoSchema = parametrosDeContratoSchema.extend({
  idAditivo: id,
  idContrato: id,
  /** 🔒 Effective FORWARD ONLY (BR-21). Periods before this keep the old set. */
  vigenteAPartirDe: dataIso,
})

export type Aditivo = z.infer<typeof aditivoSchema>
